import { createAsyncThunk, createSlice, } from '@reduxjs/toolkit'
import { AppState } from '../state/app.state';
import { deleteWhereaboutsOption, getAllWhereaboutsOptions, WhereaboutsOption } from '@features/whereabouts/services/WhereaboutOptions';
import { getIdFromKeyIfDefaultWhereaboutsOverridden } from '@shared/utils/WhereaboutsHelper';
import { naturalSort } from '@shared/utils/ArrayUtils';

enum LoadingState {
  Idle, Success, Failed, Pending,
}

export interface WhereaboutsOptionsState {
  loadingState: LoadingState;
  whereaboutsOptions: WhereaboutsOption[];
  editingWhereaboutsOptions?: WhereaboutsOption;
}

export const initialWhereaboutsOptionsState: WhereaboutsOptionsState = {
  loadingState: LoadingState.Idle,
  whereaboutsOptions: [],
}

export const loadWhereaboutsOptions: any = createAsyncThunk(
  'whereaboutsOptions/loadWhereaboutsOptions',
  async (force: boolean, thunkAPI) => {
    const state = (thunkAPI.getState() as AppState).whereaboutsOptions;
    if (state.loadingState === LoadingState.Idle || force) {
      thunkAPI.dispatch(setLoadingState(LoadingState.Pending));
      const options = await getAllWhereaboutsOptions();
      return options;
    } else {
      return state.whereaboutsOptions;
    }
  }
)

export const deleteWhereabouts: any = createAsyncThunk(
  'whereaboutsOptions/deleteWhereabouts',
  async (option: WhereaboutsOption, thunkAPI) => {
    thunkAPI.dispatch(setLoadingState(LoadingState.Pending));
    await deleteWhereaboutsOption(option);
    thunkAPI.dispatch(loadWhereaboutsOptions(true));
  }
)

export const updateSingleWhereabouts: any = createAsyncThunk(
  'whereaboutsOptions/updateSingleWhereabouts',
  async (option: WhereaboutsOption, thunkAPI) => {
    const options = (thunkAPI.getState() as AppState).whereaboutsOptions.whereaboutsOptions;
    const filtered = options.filter(wo => wo.id !== option.id);
    return [...filtered, option];
  }
)

const whereaboutsOptionsSlice = createSlice({
  name: 'whereaboutsOptions',
  initialState: initialWhereaboutsOptionsState,
  reducers: {
    setLoadingState: (state, action) => ({...state, loadingState: action.payload}),
    setEditingWhereaboutsOption: (state, action) => ({...state, editingWhereaboutsOptions: action.payload}),
    setWhereaboutsOptions: (state, action) => ({...state, whereaboutsOptions: action.payload}),
    clearEditingWhereaboutsOption: (state) => ({...state, editingWhereaboutsOptions: undefined}),
  },
  extraReducers: (builder) => {
    builder
      .addCase(loadWhereaboutsOptions.fulfilled, (state, action) => ({...state, whereaboutsOptions: action.payload, loadingState: LoadingState.Success}))
      .addCase(loadWhereaboutsOptions.rejected, (state) => ({...state, loadingState: LoadingState.Failed}))
      .addCase(updateSingleWhereabouts.fulfilled, (state, action) => ({...state, whereaboutsOptions: action.payload}));
  }
});

export const { clearEditingWhereaboutsOption, setWhereaboutsOptions } = whereaboutsOptionsSlice.actions;
const { setLoadingState } = whereaboutsOptionsSlice.actions;
export default whereaboutsOptionsSlice.reducer;

// Selectors
const selectLoadingState = (state: AppState) => state.whereaboutsOptions.loadingState;
export const selectAllSelectableWhereaboutsOptions = (state: AppState) => {
  const whereaboutsOptions = state.whereaboutsOptions.whereaboutsOptions.filter(option => option.pickable && (option.required || option.enabled))
  const sortedWhereaboutsOptions = naturalSort(whereaboutsOptions, 'label');
  return sortedWhereaboutsOptions.sort((a, b) => !!a.required && !b.required ? -1 : 1);
};
export const selectAllWhereaboutsOptions = (state: AppState) => state.whereaboutsOptions.whereaboutsOptions;
const selectWhereaboutsOptionByKey = (state: any, key: string) => {
  return state.whereaboutsOptions.whereaboutsOptions.find((option: any) => option.key === key);
};
export const selectWhereaboutsOptionById = (state: any, id: number) => {
  const overriddenId = getIdFromKeyIfDefaultWhereaboutsOverridden(state.whereaboutsOptions.whereaboutsOptions, id)
  return state.whereaboutsOptions.whereaboutsOptions.find((option: any) => option.id === (overriddenId || id));
};
const selectWhereaboutsOptionByStatus = (state: any, status: string) => {
  return state.whereaboutsOptions.whereaboutsOptions.find((option: any) => option.status === status);
};
export const selectEditingWhereaboutsOption = (state: AppState) => state.whereaboutsOptions.editingWhereaboutsOptions;

